import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { fetchInvoiceEditData, setSelectedCustomer, createCustomer, updateCustomer, setSsnValidationFailed } from '../features/customer/customer.slice';
import type { Customer } from '../features/customer/customer.service';
import LineButton from './line-button.component';
import AddCustomerModal, { EditCustomerModal } from './add-customer-modal';
import EditBillingAddressModal from './edit-billing-address-modal/edit-billing-address-modal.component';
import './customer-details.scss';
import { getItem } from '../helper/localstorage.helper';
import { getTranslatedCountryName } from '../helper/common.helper';
import { ref } from 'process';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../hooks/useOutsideClick';

interface CustomerDetailsProps {
  customer?: {
    name: string;
    id: string;
    billingAddress: string;
  };
  onCustomerChange?: (customer: any) => void;
  onAddCustomer?: (customerData: any) => void;
  onEditAddress?: () => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
  timesheetData?: any[];  // NEW - Timesheet data from Redux
  onCopyRecentInvoice?: () => void;  // NEW - open "copy recent invoice" modal (handler lives in parent)
  stepLabel?: string;    // NEW - e.g. "Step 1 of 6"
  stepTitle?: string;    // NEW - e.g. "Customer"
  stepSubtitle?: string; // NEW - e.g. "Who is this invoice for?"
}

const CustomerDetails: React.FC<CustomerDetailsProps> = ({
  customer,
  onCustomerChange,
  onAddCustomer,
  onEditAddress,
  copiedInvoiceData,
  isEditModeInvoice,
  timesheetData,
  onCopyRecentInvoice,
  stepLabel,
  stepTitle,
  stepSubtitle
}) => {
  const { t } = useTranslation();
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditBillingModal, setShowEditBillingModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditingYourReference, setIsEditingYourReference] = useState(false);
  const [isEditingCustomerReference, setIsEditingCustomerReference] = useState(false);
  const [yourReferenceValue, setYourReferenceValue] = useState('XXXXXXXX');
  const [customerReferenceValue, setCustomerReferenceValue] = useState('XXXXX XXXXXX');
  const [userID, setUserId] = useState<number | undefined>(undefined);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const editCustomerBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
    setCustomerSearch('');
  }
  ) as React.MutableRefObject<HTMLDivElement | null>;

  // Ref to track if invoice data has been fetched to prevent duplicate API calls
  const dataFetchedRef = useRef(false);
  // One-shot guard so a `?customerId=` from My Clients pre-selects that customer
  // exactly once (and the user can freely change it afterwards).
  const preselectedFromUrlRef = useRef(false);
  
  // Ref to track latest customers list for polling mechanism
  const latestCustomersRef = useRef<Customer[]>([]);

  const getUserID = async () => {
    const userItem = getItem("user");
    const userId = userItem ? JSON.parse(userItem) : null;
    if (userId && userId.userData && userId.userData.userId) {
      setUserId(userId.userData.userId);
    } else {
      setUserId(1500);
    }
  }

  useEffect(() => {
    getUserID();
  }, []);


  const dispatch = useAppDispatch();
  const customerState = useAppSelector((state: any) => state?.customer || {});
  const { customers = [], loading = false, error = null, selectedCustomer, ssnValidationFailed = false } = customerState;

  // Scroll to and blink Edit Customer button when SSN validation fails
  useEffect(() => {
    if (ssnValidationFailed && editCustomerBtnRef.current) {
      editCustomerBtnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [ssnValidationFailed]);
  
  // Check if we're in fresh creation mode
  const searchParams = new URLSearchParams(window.location.search);
  const hasEditParam = searchParams.get('edit') === 'true';
  const hasPreIdParam = searchParams.get('preId');
  const isTimesheetMode = searchParams.get('timesheet') === 'true';
  const hasInvoiceNumber = searchParams.get('invoiceNumber');
  const isFreshCreation = !hasEditParam && !hasPreIdParam && !isTimesheetMode && !hasInvoiceNumber;
  
  // Only use customer prop if not in fresh creation mode
  const currentCustomer = selectedCustomer || 
    (isFreshCreation ? null : customer) || 
    { name: t('SELECT_A_CUSTOMER'), id: '', billingAddress: '' };
  
  // Update ref whenever customers change
  useEffect(() => {
    latestCustomersRef.current = customers;
  }, [customers]);

  // Reset customer selection when selectedCustomer becomes null from Redux
  useEffect(() => {
    if (selectedCustomer === null && !copiedInvoiceData && !isEditModeInvoice) {
      // Ensure the component reflects the reset state
      if (onCustomerChange) {
        onCustomerChange(null);
      }
      // Also reset reference values
      setYourReferenceValue('XXXXXXXX');
      setCustomerReferenceValue('XXXXX XXXXXX');
    }
  }, [selectedCustomer, copiedInvoiceData, isEditModeInvoice, onCustomerChange]);

  // Handle copiedInvoiceData and auto-select customer
  useEffect(() => {
   
    // Check if we're in timesheet mode via URL parameter
    const searchParams = new URLSearchParams(window.location.search);
    const isTimesheetMode = searchParams.get('timesheet') === 'true';
    const hasEditParam = searchParams.get('edit') === 'true';
    const hasPreIdParam = searchParams.get('preId');
    const hasInvoiceNumber = searchParams.get('invoiceNumber');

    // Check if this is fresh creation (no special params)
    const isFreshCreation = !hasEditParam && !hasPreIdParam && !isTimesheetMode && !hasInvoiceNumber;
    // Customer passed from My Clients ("Create invoice" on a specific customer).
    const urlCustomerId = searchParams.get('customerId');

    // If fresh creation and no copied data:
    if (isFreshCreation && !copiedInvoiceData) {
      // A `?customerId=` was passed → pre-select THAT customer (once its data
      // loads) instead of clearing, so the chosen customer shows in the dropdown.
      if (urlCustomerId) {
        if (!customers || customers.length === 0 || loading) return; // wait for list
        if (preselectedFromUrlRef.current) return;                   // only once
        const match = customers.find((c: Customer) =>
          (c.customerId || c.id)?.toString() === urlCustomerId.toString());
        if (match) {
          preselectedFromUrlRef.current = true;
          handleCustomerSelect((match.customerId || match.id)?.toString() || '');
        }
        return;
      }
      // No customerId → clear selection immediately (fresh, empty invoice).
      dispatch(setSelectedCustomer(null));
      if (onCustomerChange) {
        onCustomerChange(null);
      }
      return; // Exit early
    }

    // Wait until customers are loaded and we're not in loading state
    if (!customers || customers.length === 0 || loading) {
      return;
    }

    // Handle timesheet data - auto-select customer from timesheet
    // Only use timesheetData if we're in timesheet mode (timesheet=true in URL)
    if (isTimesheetMode && timesheetData && timesheetData.length > 0 && timesheetData[0]?.customerId) {
      const timesheetCustomerId = timesheetData[0].customerId;
      
      const matchingCustomer = customers.find((c: Customer) => {
        const customerId = c.customerId || c.id;
        return customerId?.toString() === timesheetCustomerId?.toString();
      });

      if (matchingCustomer) {
        const customerId = matchingCustomer.customerId || matchingCustomer.id;
        handleCustomerSelect(customerId?.toString() || '');
      } else {
      }
      return; // Exit early after handling timesheet data
    }

    if (copiedInvoiceData && copiedInvoiceData.customer) {
      const customerFromInvoice = copiedInvoiceData.customer;
     

      const matchingCustomer = customers.find((c: Customer) => {
        const customerId = c.customerId || c.id;
        const matches = customerId?.toString() === customerFromInvoice.customerId?.toString() ||
          c.name?.toLowerCase() === customerFromInvoice.customerName?.toLowerCase();
        
       
        return matches;
      });

      if (matchingCustomer) {
        // Select the customer using existing handler
        const customerId = matchingCustomer.customerId || matchingCustomer.id;
        handleCustomerSelect(customerId?.toString() || '');
      } else {
        // If customer not found in list, set it directly (for display purposes)
        const customerData = {
          id: customerFromInvoice.customerId?.toString() || '',
          customerId: customerFromInvoice.customerId || 0,
          name: customerFromInvoice.customerName || customerFromInvoice.name || '',
          billingAddress: `${customerFromInvoice.addressLine1 || ''}\n${customerFromInvoice.city || ''}, ${customerFromInvoice.zipCode || ''}\n${customerFromInvoice.country || ''}`.trim(),
          email: customerFromInvoice.email || '', // Add customer email
          secondaryEmailAddress: customerFromInvoice.secondaryEmailAddress || '',
          paymentTerm: customerFromInvoice.paymentTerm, // Add paymentTerm for due date calculation
        };

        dispatch(setSelectedCustomer(customerData));

        if (onCustomerChange) {
          onCustomerChange(customerData);
        }
      }

      // Set reference values if available
      if (copiedInvoiceData.yourReference) {
        setYourReferenceValue(copiedInvoiceData.yourReference);
      }
      if (copiedInvoiceData.customerReference) {
        setCustomerReferenceValue(copiedInvoiceData.customerReference);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedInvoiceData, isEditModeInvoice, customers, loading, timesheetData]);

  useEffect(() => {
    if (userID && !dataFetchedRef.current) {
      dataFetchedRef.current = true;
      dispatch(fetchInvoiceEditData(userID));
    }
  }, [dispatch, userID]);

  const handleCustomerSelect = (customerId: string) => {
    // Use latestCustomersRef to get the most up-to-date customer list
    const currentCustomers = latestCustomersRef.current;
    
    const selected = currentCustomers.find((c: Customer) => {
      const customerIdToCheck = c.customerId || c.id;
      return customerIdToCheck?.toString() === customerId;
    });
   
    console.log('=== Customer Selection Debug ===');
    console.log('Selected customer from API:', selected);
    console.log('hasAnyInvoice from API:', selected?.hasAnyInvoice);
    console.log('================================');
   
    if (selected) {
      const customerData = {
        id: (selected.customerId || selected.id)?.toString() || '',
        customerId: selected.customerId || selected.id || 0,
        name: selected.name,
        billingAddress: selected.address || 'No address provided',
        email: selected.email || '', // Add customer email for API payload
        secondaryEmailAddress: selected.secondaryEmailAddress || '',
        accountType: selected.accountType, // Add accountType for RUT/ROT validation
        country: selected.country, // Add country for RUT/ROT validation
        paymentTerm: selected.paymentTerm, // Add paymentTerm for due date calculation
        hasAnyInvoice: selected.hasAnyInvoice, // Add hasAnyInvoice for warning condition
      };
      dispatch(setSelectedCustomer(customerData));

      // Update reference values from selected customer
      if (selected.reference) {
        setYourReferenceValue(selected.reference);
      }

      if (onCustomerChange) {
        onCustomerChange(customerData);
      }
     }
  };

  const handleAddCustomerClick = () => {
    setIsEditMode(false);
    setShowAddCustomerModal(true);
  };

  const handleEditCustomerClick = () => {
    setIsEditMode(true);
    setShowEditCustomerModal(true);
    // Stop blinking when user clicks Edit Customer
    if (ssnValidationFailed) {
      dispatch(setSsnValidationFailed(false));
    }
  };

  const handleEditAddressClick = () => {
    setShowEditBillingModal(true);
  };

  const getCurrentCustomerBillingData = () => {
    if (!selectedCustomer?.id) return undefined;
    const fullCustomerData = customers.find((c: Customer) => {
      const customerIdToCheck = c.customerId || c.id;
      return customerIdToCheck?.toString() === selectedCustomer.id;
    });

    if (fullCustomerData) {
      const mapCountryToCode = (country: string) => {
        const countryMappings: { [key: string]: string } = {
          'Sweden': 'SE',
          'Norway': 'NO',
          'Denmark': 'DK',
          'Finland': 'FI',
          'United States': 'US',
          'USA': 'US',
          'United Kingdom': 'GB',
          'Germany': 'DE',
          'Canada': 'CA'
        };
        if (Object.values(countryMappings).includes(country)) {
          return country;
        }
        return countryMappings[country] || country;
      };
      return {
        country: mapCountryToCode(fullCustomerData.country || ''),
        stateCounty: '',
        city: fullCustomerData.city || '',
        addressLine: fullCustomerData.address || '',
        zipCode: fullCustomerData.zipCode || ''
      };
    }

    return;
  };

  const handleEditBillingModalClose = () => {
    setShowEditBillingModal(false);
  };

  const handleBillingAddressSave = (billingData: any) => {
    if (onEditAddress) {
      onEditAddress();
    }
    setShowEditBillingModal(false);
  };

  const handleEditCustomerReferenceClick = () => {
    setIsEditingCustomerReference(true);
  };

  const handleYourReferenceSave = () => {
    setIsEditingYourReference(false);
  };

  const handleCustomerReferenceSave = () => {
    setIsEditingCustomerReference(false);
  };

  const handleYourReferenceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleYourReferenceSave();
    } else if (e.key === 'Escape') {
      setYourReferenceValue('XXXXXXXX'); // Reset to original value
      setIsEditingYourReference(false);
    }
  };

  const handleCustomerReferenceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomerReferenceSave();
    } else if (e.key === 'Escape') {
      setCustomerReferenceValue('XXXXX XXXXXX'); // Reset to original value
      setIsEditingCustomerReference(false);
    }
  };

  // Update reference values when selected customer changes
  useEffect(() => {
    if (selectedCustomer ) {
      const fullCustomerData = customers.find((c: Customer) => {
        const customerIdToCheck = c.customerId || c.id;
        return customerIdToCheck?.toString() === selectedCustomer.id;
      });

      if (fullCustomerData) {
        // Update Your Reference from customer data
        if (fullCustomerData.reference) {
          setYourReferenceValue(fullCustomerData.reference);
        }
      }
    }
  }, [selectedCustomer, customers]);


  return (
    <Card className="invoice-card mb-4 customer-details cust-step">
      <Card.Body className="card-body cust-step-body">
        {/* Step header — heading on the left, quick actions on the right */}
        {(stepTitle || stepLabel || onCopyRecentInvoice) && (
          <div className="cust-step-header">
            <div className="cust-step-heading iw-head-with-icon">
              <span className="iw-head-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              </span>
              <div className="iw-head-text">
                {stepLabel && (
                  <span className="cust-step-pill"><span className="cust-step-dot" />{stepLabel}</span>
                )}
                {stepTitle && <h2 className="cust-step-title">{stepTitle}</h2>}
                {stepSubtitle && <p className="cust-step-sub">{stepSubtitle}</p>}
              </div>
            </div>
            <div className="cust-step-actions">
              <button type="button" className="cust-new-btn" onClick={handleAddCustomerClick} disabled={isEditModeInvoice}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3.33v9.34M3.33 8h9.34" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
                {t('NEW_CUSTOMER')}
              </button>
            </div>
          </div>
        )}

        <label className="input-label">{t('SELECT_CUSTOMER')}</label>
        <div className="custom-dropdown cust-select" ref={dropdownRef}>
              <div
                className={`dropdown-trigger ${isDropdownOpen ? 'open' : ''} ${isEditModeInvoice ? 'disabled' : ''}`}
                onClick={() => !isEditModeInvoice && setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="cust-avatar" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                    <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="cust-trigger-text">
                  <span className="cust-trigger-name">
                    {loading ? t('LOADING_CUSTOMERS') : (currentCustomer.name || t('SELECT_A_CUSTOMER'))}
                  </span>
                  {selectedCustomer && currentCustomer.id && (
                    <span className="cust-trigger-id">{t('CUSTOMER_ID')}: {currentCustomer.id}</span>
                  )}
                </span>
                <svg
                  className={`dropdown-arrow cust-chevron ${isDropdownOpen ? 'rotated' : ''}`}
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M6 8L10 4L14 8M6 12l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isDropdownOpen && !loading && (
                <div className="dropdown-list">
                  <div className="dropdown-search">
                    <svg className="dropdown-search-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="m19 19-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                      type="text"
                      className="dropdown-search-input"
                      value={customerSearch}
                      autoFocus
                      placeholder={t('SEARCH_CUSTOMER') as string || 'Search customer...'}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="dropdown-content">
                    {(() => {
                      const term = customerSearch.trim().toLowerCase();
                      const filtered = term
                        ? customers.filter((c: Customer) => (c.name || '').toLowerCase().includes(term))
                        : customers;
                      if (filtered.length === 0) {
                        return <div className="dropdown-empty">{t('NO_CUSTOMERS_FOUND') as string || 'No customers found'}</div>;
                      }
                      return filtered.map((cust: Customer) => {
                        const customerId = cust.customerId || cust.id;
                        const isSelected = currentCustomer.id === customerId?.toString();
                        return (
                          <div
                            key={customerId}
                            className={`list-item cust-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              handleCustomerSelect(customerId?.toString() || '');
                              setIsDropdownOpen(false);
                              setCustomerSearch('');
                            }}
                          >
                            <span className="cust-option-avatar" aria-hidden="true">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                                <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </span>
                            <span className="cust-option-text">
                              <span className="cust-option-name">{cust.name}</span>
                              {customerId && (
                                <span className="cust-option-id">{t('CUSTOMER_ID')}: {customerId}</span>
                              )}
                            </span>
                            {isSelected && (
                              <span className="cust-option-check" aria-hidden="true">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}
            </div>

        {/* Two-card layout — Customer summary + Quick actions */}
        <div className="cust-grid">
          <div className="cust-info-card cust-summary">
            <div className="cust-card-head">
              <span className="cust-card-label">{t('CUSTOMER_LABEL')}</span>
              {selectedCustomer && (
                <button
                  ref={editCustomerBtnRef}
                  type="button"
                  onClick={handleEditCustomerClick}
                  className={`cust-edit-btn${ssnValidationFailed ? ' edit-customer-btn--blink' : ''}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {t('EDIT')}
                </button>
              )}
            </div>

            {selectedCustomer ? (
              <>
                <div className="cust-name">{currentCustomer.name}</div>
                <div className="cust-id-line">{t('CUSTOMER_ID')} · {currentCustomer.id || t('NOT_SELECTED')}</div>

                {/* All available customer details — shown as an attractive icon grid */}
                {(currentCustomer.email || currentCustomer.secondaryEmailAddress || currentCustomer.country || currentCustomer.paymentTerm || currentCustomer.accountType !== undefined) && (
                  <div className="cust-details">
                    {currentCustomer.email && (
                      <div className="cust-detail">
                        <span className="cust-detail-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></svg>
                        </span>
                        <div className="cust-detail-text">
                          <span className="cust-detail-label">{t('EMAIL')}</span>
                          <span className="cust-detail-val">{currentCustomer.email}</span>
                        </div>
                      </div>
                    )}
                    {currentCustomer.secondaryEmailAddress && (
                      <div className="cust-detail">
                        <span className="cust-detail-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="m22 6-10 7L2 6" /></svg>
                        </span>
                        <div className="cust-detail-text">
                          <span className="cust-detail-label">{t('SECONDARY_EMAIL')}</span>
                          <span className="cust-detail-val">{currentCustomer.secondaryEmailAddress}</span>
                        </div>
                      </div>
                    )}
                    {currentCustomer.country && (
                      <div className="cust-detail">
                        <span className="cust-detail-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                        </span>
                        <div className="cust-detail-text">
                          <span className="cust-detail-label">{t('COUNTRY')}</span>
                          <span className="cust-detail-val">{getTranslatedCountryName(t, currentCustomer.country, currentCustomer.countryCode)}</span>
                        </div>
                      </div>
                    )}
                    {currentCustomer.paymentTerm && (
                      <div className="cust-detail">
                        <span className="cust-detail-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                        </span>
                        <div className="cust-detail-text">
                          <span className="cust-detail-label">{t('PAYMENT_TERM')}</span>
                          <span className="cust-detail-val">{currentCustomer.paymentTerm} {t('DAYS')}</span>
                        </div>
                      </div>
                    )}
                    {currentCustomer.accountType !== undefined && currentCustomer.accountType !== null && (
                      <div className="cust-detail">
                        <span className="cust-detail-ic">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                        </span>
                        <div className="cust-detail-text">
                          <span className="cust-detail-label">{t('ACCOUNT_TYPE')}</span>
                          <span className="cust-detail-val">{currentCustomer.accountType === 1 ? t('CUST_PRIVATE') : t('CUST_COMPANY')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="cust-fields-row">
                <div className="cust-field">
                  <span className="cust-field-label">{t('ADDRESS_LABEL')}</span>
                  <div className="cust-field-value">
                    {currentCustomer.billingAddress ? (
                      currentCustomer.billingAddress.split('\n').map((line: string, index: number) => (
                        <span key={index}>
                          {line}
                          {index < currentCustomer.billingAddress.split('\n').length - 1 && <br />}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted">{t('NO_BILLING_ADDRESS_AVAILABLE')}</span>
                    )}
                    <Button variant="link" className="edit-address-btn cust-inline-edit" onClick={handleEditAddressClick} disabled={isEditModeInvoice}> {t('EDIT')}</Button>
                  </div>
                </div>

                <div className="cust-field">
                  <span className="cust-field-label">{t('REFERENCE_LABEL')}</span>
                  <div className="reference-numbers">
            {/* <div className={`reference-item ${isEditingYourReference ? 'editing' : ''}`}>
              {!isEditingYourReference && (
                <Button
                  variant="link"
                  className="edit-icon-btn p-0"
                  onClick={handleEditYourReferenceClick}
                  disabled={isEditModeInvoice}
                >
                  <svg className="edit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#414651" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              )}
              {isEditingYourReference ? (
                <div className="inline-edit-container">
                  <input
                    type="text"
                    value={yourReferenceValue}
                    onChange={(e) => setYourReferenceValue(e.target.value)}
                    onKeyDown={handleYourReferenceKeyPress}
                    autoFocus
                    className="inline-edit-input"
                    placeholder={t('ENTER_YOUR_REFERENCE_NO')}
                  />
                  <Button
                    onClick={handleYourReferenceSave}
                    className="inline-edit-save-btn"
                  >
                    {t('SAVE')}
                  </Button>
                </div>
              ) : (
                <span>{t('YOUR_REFERENCE')}: {yourReferenceValue}</span>
              )}
            </div> */}
            <div className={`reference-item ${isEditingCustomerReference ? 'editing' : ''}`}>
              {!isEditingCustomerReference && (
                <Button
                  variant="link"
                  className="edit-icon-btn p-0"
                  onClick={handleEditCustomerReferenceClick}
                  disabled={isEditModeInvoice}
                >
                  <svg className="edit-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#414651" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Button>
              )}
              {isEditingCustomerReference ? (
                <div className="inline-edit-container">
                  <input
                    type="text"
                    value={customerReferenceValue}
                    onChange={(e) => setCustomerReferenceValue(e.target.value)}
                    onKeyDown={(e) => {
                      // Block non-Latin characters
                      const allowedCharacters = /^[a-zA-Z0-9\s,.\-/']$/;
                      if (
                        e.key !== 'Backspace' &&
                        !allowedCharacters.test(e.key) &&
                        !['ArrowLeft', 'ArrowRight', 'Tab', 'Delete', 'Control', 'v', 'Meta', 'c', 'x', 'a', 'Enter', 'Escape'].includes(e.key) &&
                        !(e.ctrlKey || e.metaKey)
                      ) {
                        e.preventDefault();
                        return;
                      }
                      // Handle Enter and Escape keys
                      handleCustomerReferenceKeyPress(e);
                    }}
                    autoFocus
                    className="inline-edit-input"
                    placeholder={t('ENTER_CUSTOMER_REFERENCE_NAME')}
                  />
                  <Button
                    onClick={handleCustomerReferenceSave}
                    className="inline-edit-save-btn"
                  >
                    {t('SAVE')}
                  </Button>
                </div>
              ) : (
                <span className="cust-ref-value">{yourReferenceValue}</span>
              )}
            </div>
                  </div>
                </div>
                </div>
              </>
            ) : (
              <div className="cust-empty-summary">{t('SELECT_A_CUSTOMER')}</div>
            )}
          </div>
        </div>
      </Card.Body>
      <AddCustomerModal
        show={showAddCustomerModal}
        onHide={() => setShowAddCustomerModal(false)}
        onSave={(customerData) => { 
          setShowAddCustomerModal(false);
          
          // Extract customer identifiers from the form data
          const customerName = customerData?.customerName || '';
          const customerEmail = customerData?.contactEmail || '';
          const customerSSN = customerData?.ssn || '';
          
          // Polling mechanism to wait for customer to appear in Redux state
          const maxRetries = 50; // Maximum 50 retries (25 seconds total)
          const retryInterval = 500; // Check every 500ms
          let retryCount = 0;
          
          const pollForNewCustomer = () => {
            retryCount++;
            // Get latest customers from ref (not closure)
            const currentCustomers = latestCustomersRef.current;
            
            
            // Try to find the newly created customer by name, email, or SSN
            const foundCustomer = currentCustomers.find((c: Customer) => {
              if (!c) return false;
              const nameMatch = c.name?.toLowerCase() === customerName?.toLowerCase();
              const emailMatch = c.email?.toLowerCase() === customerEmail?.toLowerCase();
              const ssnMatch = customerSSN && c.ssn === customerSSN;
              
              return nameMatch || emailMatch || ssnMatch;
            });
            
            if (foundCustomer) {
             
              const customerId = foundCustomer.customerId || foundCustomer.id;
              
              // Verify and select the customer
              const attemptSelection = (attemptsLeft = 3) => {
                // Re-fetch to ensure we have latest state
                const latestCustomers = latestCustomersRef.current;
                const selected = latestCustomers.find((c: Customer) => {
                  if (!c) return false;
                  const customerIdToCheck = c.customerId || c.id;
                  return customerIdToCheck?.toString() === customerId?.toString();
                });
                
                if (selected) {
                  handleCustomerSelect(customerId?.toString() || '');
                } else if (attemptsLeft > 0) {
                  setTimeout(() => attemptSelection(attemptsLeft - 1), 500);
                }
              };
              
              attemptSelection();
            } else if (retryCount >= maxRetries) {
              toast.error(String(t('CUSTOMER_SELECTION_FAILED') || 'Customer was created but could not be selected automatically. Please select it manually.'));
              
              // Last resort: try to select the last customer in the list
              if (currentCustomers.length > 0) {
                const lastCustomer = currentCustomers[currentCustomers.length - 1];
                if (!lastCustomer) {
                  return;
                }
                const lastCustomerId = lastCustomer.customerId || lastCustomer.id;
                handleCustomerSelect(lastCustomerId?.toString() || '');
              }
            } else {
              // Customer not found yet, retry after interval
              setTimeout(pollForNewCustomer, retryInterval);
            }
          };
          
         setTimeout(pollForNewCustomer, 1000);
        }}
      />
      {showEditCustomerModal && (
        <EditCustomerModal
          show={showEditCustomerModal}
          onHide={() => {
            setShowEditCustomerModal(false);
            setSelectedCustomer(null);
          }}
          onSave={(customerData) => {
            setShowEditCustomerModal(false);
            // Auto-select the updated customer after API refresh completes
            setTimeout(() => {
              if (customerData?.customerId) {
                handleCustomerSelect(customerData.customerId.toString());
              }
            }, 500);
          }}
          selectedCustomer={selectedCustomer?.id}
        />
      )}

      <EditBillingAddressModal
        show={showEditBillingModal}
        onHide={handleEditBillingModalClose}
        onSave={handleBillingAddressSave}
        initialData={getCurrentCustomerBillingData()}
        customerId={selectedCustomer?.id ? parseInt(selectedCustomer.id) : undefined}
      />
    </Card>
  );
};

export default CustomerDetails;